import { useMemo } from "react";

import { KnowledgeDbIcpApi } from "../schema/knowledgeDbApi";
import { CompanySegment, SegmentContent } from "../schema/knowledgeDb";
import { apiIcpToSegmentContent, dedupeContentsBySegment } from "../utils/transformers";

interface UseKnowledgeDbContentsResult {
	/** One content per company×persona segment — what the sidebar and the content area render. */
	contents: SegmentContent[];
	/** The subset whose segment still exists in the score data — what the tab badges count. */
	visibleContents: SegmentContent[];
}

/**
 * Derive the Knowledge DB segment contents from the ICP response.
 *
 * Both lists come from one deduped source so the sidebar checkmarks and the tab badge can never
 * disagree about a segment's status (PREC-3700).
 */
export const useKnowledgeDbContents = (
	icps: KnowledgeDbIcpApi[] | undefined,
	companySegments: CompanySegment[]
): UseKnowledgeDbContentsResult => {
	const contents = useMemo(
		() => dedupeContentsBySegment((icps ?? []).map(apiIcpToSegmentContent)),
		[icps]
	);

	// Only ICPs that match a visible company×persona category from the score data.
	// Orphan ICPs (e.g. from deleted score categories) should not affect tab badges.
	const visibleContents = useMemo(() => {
		const validPairs = new Set<string>();

		for (const company of companySegments) {
			for (const persona of company.personas) {
				validPairs.add(`${company.id}:${persona.id}`);
			}
		}

		return contents.filter((content) =>
			validPairs.has(`${content.scoreCompanyCategoryId}:${content.scorePersonaCategoryId}`)
		);
	}, [contents, companySegments]);

	return { contents, visibleContents };
};
